// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import { useState } from "react"
import { useCart, dispatchCart, countChanges, type CartItem } from "./edit-cart"
import { useApplyCart } from "./use-apply-cart"

/**
 * Mobile replacement for the dense desktop cart list (CockpitCart). Surfaces the
 * same staged-edits batch as a one-line "{n} changes · Save" bar that drives the
 * exact same PR/CMS apply via useApplyCart — so mobile keeps single-PR batching,
 * it just doesn't show the cramped multi-row list. Tapping the count expands a
 * lightweight list (re-edit / remove). Renders nothing when the cart is empty and
 * there's no result to keep showing. Same props as CockpitCart.
 */
export function MobilePendingBar({
  identity,
  currentRef,
  env,
  preview,
  onTogglePreview,
  onEditItem,
}: {
  identity: { idToken: string }
  currentRef: string
  env: string
  preview: boolean
  onTogglePreview: (v: boolean) => void
  onEditItem?: (item: CartItem) => void
}) {
  const items = useCart()
  const { busy, result, setResult, apply } = useApplyCart({
    idToken: identity.idToken,
    currentRef,
    env,
  })
  const [listOpen, setListOpen] = useState(false)

  // Keep the bar mounted after a successful apply so the PR link / result stays
  // visible (the cart clears on success, but the result must persist).
  if (!items.length && !result) return null

  const intlItems = items.filter((i) => i.source === "intl")
  const cmsItems = items.filter((i) => i.source === "cms")
  const changes = countChanges(items)

  return (
    <div data-ahead-editor-ui className="ahead-mobile-pending">
      {items.length ? (
        <>
          <div className="ahead-mobile-pending-row">
            <button
              type="button"
              className="ahead-mobile-pending-count"
              aria-expanded={listOpen}
              onClick={() => setListOpen((o) => !o)}
            >
              {changes} change{changes > 1 ? "s" : ""} {listOpen ? "▾" : "▸"}
            </button>
            <button
              type="button"
              className="ahead-mobile-pending-save"
              disabled={busy}
              onClick={() => apply(intlItems, cmsItems)}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>

          {listOpen ? (
            <ul className="ahead-mobile-pending-list">
              {items.map((i) => (
                <li key={i.key} className="ahead-mobile-pending-item">
                  <button
                    type="button"
                    className="ahead-mobile-pending-item-edit"
                    onClick={() => onEditItem?.(i)}
                  >
                    <code>
                      {i.source === "cms"
                        ? i.key
                        : i.key.split(".").slice(-2).join(".")}
                    </code>{" "}
                    <span style={{ color: "#aaa" }}>
                      ({Object.keys(i.values).join(",")})
                    </span>
                  </button>
                  <button
                    type="button"
                    className="ahead-mobile-pending-item-remove"
                    aria-label="Remove from cart"
                    onClick={() => dispatchCart({ type: "remove", key: i.key })}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <label className="ahead-mobile-pending-preview">
            <input
              type="checkbox"
              checked={preview}
              onChange={(e) => onTogglePreview(e.target.checked)}
            />
            Preview changes on page
          </label>
        </>
      ) : null}

      {/* Post-apply result. A success only shows once the cart is empty, so a
          fresh edit replaces it instead of letting a stale "Done" linger. A
          FAILURE shows even while items remain (the cart isn't cleared on error). */}
      {result && (!result.ok || !items.length) ? (
        <div className="ahead-mobile-pending-result">
          <div
            className="ahead-mobile-pending-result-head"
            role="status"
            aria-live="polite"
          >
            <strong style={{ color: result.ok ? "#070" : "#b00" }}>
              {result.ok ? "Done" : "Couldn’t apply"}
            </strong>
            <button
              type="button"
              className="ahead-mobile-pending-result-dismiss"
              aria-label="Dismiss"
              onClick={() => setResult(null)}
            >
              ×
            </button>
          </div>
          {result.prUrl ? (
            <a
              href={result.prUrl}
              target="_blank"
              rel="noreferrer"
              className="ahead-mobile-pending-prlink"
            >
              View PR →
            </a>
          ) : null}
          <div style={{ wordBreak: "break-all", color: "#444", marginTop: 4 }}>
            {result.msg}
          </div>
        </div>
      ) : null}
    </div>
  )
}
